import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { IoWarning, IoCheckmarkCircle, IoCloseCircle, IoHelpCircle, IoCodeSlash, IoDownload } from 'react-icons/io5';

interface InventoryItem {
  name: string;
  filename: string;
  fileChecksum: string | null;
  inFilesystem: boolean;
  inDatabase: boolean;
  status: string;
  appliedAt: string | null;
  appliedBy: string | null;
  duration: number | null;
  checksumMatch: boolean | null;
  isGhost?: boolean;
  isReversible?: boolean;
  isDestructive?: boolean;
}

interface InventoryData {
  directory: string;
  items: InventoryItem[];
  summary: {
    totalFiles: number;
    totalInDb: number;
    applied: number;
    pending: number;
    failed: number;
    checksumMismatches: number;
    ghosts: number;
  };
}

const InventoryMatrix: React.FC = () => {
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [diffData, setDiffData] = useState<any>(null);
  const [selectedMigrations, setSelectedMigrations] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'apply' | 'rollback' | null>(null);
  const [preflightData, setPreflightData] = useState<any>(null);
  const [showPreflightModal, setShowPreflightModal] = useState(false);

  const { data, isLoading, refetch } = useQuery<InventoryData>(
    'migrations-inventory',
    async () => {
      const response = await axios.get('/api/v1/admin/migrations/inventory');
      return response.data;
    },
    {
      refetchInterval: false,         // Disable auto-refresh
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  );

  if (isLoading) {
    return <div className="p-4">Loading inventory...</div>;
  }

  const filteredItems = data?.items.filter(item => {
    // Search filter
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Status filter
    if (filter === 'pending' && item.status !== 'pending') return false;
    if (filter === 'failed' && item.status !== 'failed') return false;
    if (filter === 'drift' && item.checksumMatch !== false) return false;
    if (filter === 'ghost' && !item.isGhost) return false;

    return true;
  }) || [];

  const handleViewDiff = async (name: string) => {
    try {
      const response = await axios.get(`/api/v1/admin/migrations/${name}/diff`);
      setDiffData(response.data);
      setDiffModalOpen(true);
    } catch (error) {
      console.error('Error fetching diff:', error);
      alert('Failed to load diff');
    }
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const response = await axios.get(`/api/v1/admin/migrations/inventory/export?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        // Download CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `migrations-inventory-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Download JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `migrations-inventory-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export inventory');
    }
  };

  const toggleSelection = (name: string) => {
    const newSelection = new Set(selectedMigrations);
    if (newSelection.has(name)) {
      newSelection.delete(name);
    } else {
      newSelection.add(name);
    }
    setSelectedMigrations(newSelection);
  };

  const handleBulkApply = async (target: 'dev' | 'prod') => {
    if (selectedMigrations.size === 0) return;

    const names = Array.from(selectedMigrations);
    
    // Check preflight for first migration
    try {
      const firstMigration = names[0];
      const preflightResponse = await axios.get(`/api/v1/admin/migrations/${firstMigration}/preflight?target=${target}`);
      setPreflightData({ ...preflightResponse.data, target, names });
      setShowPreflightModal(true);
      setBulkAction('apply');
    } catch (error) {
      console.error('Error checking preflight:', error);
      alert('Failed to run preflight checks');
    }
  };

  const confirmBulkApply = async () => {
    if (!preflightData) return;

    try {
      const response = await axios.post('/api/v1/admin/migrations/bulk/apply', {
        target: preflightData.target,
        names: preflightData.names
      });

      alert(`Bulk apply: ${response.data.summary.succeeded}/${response.data.summary.total} succeeded`);
      setSelectedMigrations(new Set());
      setShowPreflightModal(false);
      refetch();
    } catch (error: any) {
      console.error('Error in bulk apply:', error);
      if (error.response?.data?.requiresApproval) {
        alert(`Approval required for destructive migrations: ${error.response.data.destructiveMigrations.join(', ')}`);
      } else {
        alert('Failed to apply migrations');
      }
    }
  };

  const handleBulkRollback = async () => {
    if (selectedMigrations.size === 0) return;

    if (!window.confirm(`Rollback ${selectedMigrations.size} migrations?`)) return;

    try {
      const response = await axios.post('/api/v1/admin/migrations/bulk/rollback', {
        target: 'dev', // Default to dev for safety
        names: Array.from(selectedMigrations)
      });

      alert(`Bulk rollback: ${response.data.summary.succeeded}/${response.data.summary.total} succeeded`);
      setSelectedMigrations(new Set());
      refetch();
    } catch (error: any) {
      console.error('Error in bulk rollback:', error);
      alert(error.response?.data?.error || 'Failed to rollback migrations');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Migration Inventory</h2>
          <p className="text-sm text-gray-600">Filesystem ⇄ Database Matrix</p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-3">
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-blue-500">
          <div className="text-2xl font-bold text-blue-600">{data?.summary.totalFiles || 0}</div>
          <div className="text-xs text-gray-600">Files</div>
        </div>
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-green-500">
          <div className="text-2xl font-bold text-green-600">{data?.summary.applied || 0}</div>
          <div className="text-xs text-gray-600">Applied</div>
        </div>
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-yellow-500">
          <div className="text-2xl font-bold text-yellow-600">{data?.summary.pending || 0}</div>
          <div className="text-xs text-gray-600">Pending</div>
        </div>
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-red-500">
          <div className="text-2xl font-bold text-red-600">{data?.summary.failed || 0}</div>
          <div className="text-xs text-gray-600">Failed</div>
        </div>
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-orange-500">
          <div className="text-2xl font-bold text-orange-600">{data?.summary.checksumMismatches || 0}</div>
          <div className="text-xs text-gray-600">Drift</div>
        </div>
        <div className="p-3 bg-white rounded shadow text-center border-l-4 border-purple-500">
          <div className="text-2xl font-bold text-purple-600">{data?.summary.ghosts || 0}</div>
          <div className="text-xs text-gray-600">Ghosts</div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedMigrations.size > 0 && (
        <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="font-medium text-blue-900">
                {selectedMigrations.size} migration(s) selected
              </span>
              <button
                onClick={() => setSelectedMigrations(new Set())}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkApply('dev')}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                Apply to Dev
              </button>
              <button
                onClick={() => handleBulkApply('prod')}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
              >
                Apply to Prod
              </button>
              <button
                onClick={handleBulkRollback}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
              >
                Rollback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters & Export */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-3 items-center flex-wrap justify-between">
          <div className="flex gap-3 items-center flex-wrap">
            <input
              type="text"
              placeholder="Search migrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 min-w-[200px] border rounded px-3 py-2"
          />
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-2 rounded text-sm ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-2 rounded text-sm ${
              filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={`px-3 py-2 rounded text-sm ${
              filter === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Failed
          </button>
          <button
            onClick={() => setFilter('drift')}
            className={`px-3 py-2 rounded text-sm ${
              filter === 'drift' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Checksum Drift
          </button>
          <button
            onClick={() => setFilter('ghost')}
            className={`px-3 py-2 rounded text-sm ${
              filter === 'ghost' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            Ghosts
          </button>
          </div>
          
          {/* Export Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('json')}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              title="Export as JSON"
            >
              <IoDownload /> JSON
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              title="Export as CSV"
            >
              <IoDownload /> CSV
            </button>
          </div>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={filteredItems.length > 0 && filteredItems.every(item => !item.isGhost && selectedMigrations.has(item.name))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newSelection = new Set(selectedMigrations);
                        filteredItems.filter(item => !item.isGhost).forEach(item => newSelection.add(item.name));
                        setSelectedMigrations(newSelection);
                      } else {
                        setSelectedMigrations(new Set());
                      }
                    }}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Migration
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applied At
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applied By
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Checksum
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr key={item.name} className={item.isGhost ? 'bg-purple-50' : ''}>
                  <td className="px-4 py-3 text-center">
                    {!item.isGhost && (
                      <input
                        type="checkbox"
                        checked={selectedMigrations.has(item.name)}
                        onChange={() => toggleSelection(item.name)}
                        className="cursor-pointer"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.isGhost && <IoWarning className="text-purple-600" />}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{item.filename}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.appliedAt ? new Date(item.appliedAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.appliedBy || '-'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {item.duration ? `${item.duration}ms` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ChecksumBadge item={item} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-2 justify-center">
                      {!item.isGhost && (
                        <button
                          onClick={() => handleViewDiff(item.name)}
                          className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-xs font-medium"
                          title="View Diff"
                        >
                          <IoCodeSlash /> Diff
                        </button>
                      )}
                      {item.status === 'pending' && !item.isGhost && (
                        <button className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                          Apply
                      </button>
                    )}
                      {item.status === 'applied' && item.isReversible && (
                        <button className="text-orange-600 hover:text-orange-800 text-xs font-medium">
                          Rollback
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No migrations match the current filter
            </div>
          )}
        </div>
      </div>

      {/* Ghosts Warning */}
      {data && data.summary.ghosts > 0 && (
        <div className="p-4 bg-purple-50 border border-purple-300 rounded-lg">
          <div className="flex items-start gap-2">
            <IoWarning className="text-purple-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-purple-900">
                {data.summary.ghosts} Ghost Migration{data.summary.ghosts > 1 ? 's' : ''} Detected
              </p>
              <p className="text-sm text-purple-800 mt-1">
                These migrations exist in the database but the SQL file is missing from the filesystem.
                This usually means files were deleted or renamed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Drift Warning */}
      {data && data.summary.checksumMismatches > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-300 rounded-lg">
          <div className="flex items-start gap-2">
            <IoWarning className="text-orange-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-orange-900">
                {data.summary.checksumMismatches} Checksum Mismatch{data.summary.checksumMismatches > 1 ? 'es' : ''} Detected
              </p>
              <p className="text-sm text-orange-800 mt-1">
                The SQL file content has changed since these migrations were applied.
                This could indicate manual database changes or file modifications.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Diff Modal */}
      {diffModalOpen && diffData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDiffModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold text-gray-800">Migration Diff: {diffData.name}</h3>
              <button onClick={() => setDiffModalOpen(false)} className="text-gray-600 hover:text-gray-800 text-2xl">
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Status Info */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded">
                <div>
                  <span className="text-sm text-gray-600">File Checksum:</span>
                  <div className="font-mono text-xs">{diffData.fileChecksum}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">DB Checksum:</span>
                  <div className="font-mono text-xs">{diffData.dbChecksum || 'Not stored'}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <div className="font-semibold">{diffData.status}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Applied:</span>
                  <div className="text-sm">{diffData.appliedAt ? new Date(diffData.appliedAt).toLocaleString() : 'Not applied'}</div>
                </div>
              </div>

              {/* Drift Warning */}
              {diffData.isDrift && (
                <div className="p-3 bg-orange-50 border border-orange-300 rounded flex items-start gap-2">
                  <IoWarning className="text-orange-600 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium text-orange-900">Checksum Drift Detected!</p>
                    <p className="text-sm text-orange-800 mt-1">
                      The file has been modified since it was applied to the database.
                    </p>
                    {diffData.diffSummary && <p className="text-xs text-orange-700 mt-1">{diffData.diffSummary}</p>}
                  </div>
                </div>
              )}

              {/* Unified Diff */}
              {diffData.diff && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Unified Diff:</h4>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-xs font-mono max-h-64">
                    {diffData.diff}
                  </pre>
                </div>
              )}

              {/* SQL Content */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Current File Content:</h4>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto text-xs font-mono max-h-96">
                  {diffData.fileContent}
                </pre>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setDiffModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preflight Modal */}
      {showPreflightModal && preflightData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPreflightModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold text-gray-800">Preflight Checks</h3>
              <button onClick={() => setShowPreflightModal(false)} className="text-gray-600 hover:text-gray-800 text-2xl">
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Summary */}
              <div className={`p-4 rounded-lg ${preflightData.ok ? 'bg-green-50 border border-green-300' : 'bg-orange-50 border border-orange-300'}`}>
                <div className="flex items-center gap-2">
                  {preflightData.ok ? (
                    <IoCheckmarkCircle className="text-green-600" size={24} />
                  ) : (
                    <IoWarning className="text-orange-600" size={24} />
                  )}
                  <div>
                    <p className={`font-bold ${preflightData.ok ? 'text-green-900' : 'text-orange-900'}`}>
                      {preflightData.ok ? '✅ Ready to proceed' : '⚠️ Review warnings before proceeding'}
                    </p>
                    <p className={`text-sm mt-1 ${preflightData.ok ? 'text-green-800' : 'text-orange-800'}`}>
                      {preflightData.recommendation}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {preflightData.warnings && preflightData.warnings.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700">Warnings:</h4>
                  {preflightData.warnings.map((warning: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-3 rounded border ${
                        warning.severity === 'high'
                          ? 'bg-red-50 border-red-300'
                          : 'bg-yellow-50 border-yellow-300'
                      }`}
                    >
                      <p className="font-medium">{warning.message}</p>
                      {warning.details && (
                        <ul className="text-sm mt-1 list-disc list-inside">
                          {warning.details.map((detail: string, i: number) => (
                            <li key={i}>{detail}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* System Checks */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Blocking Locks:</p>
                  <p className="text-2xl font-bold">{preflightData.checks?.locks || 0}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Long-Running TXs:</p>
                  <p className="text-2xl font-bold">{preflightData.checks?.longRunning || 0}</p>
                </div>
              </div>

              {/* Affected Tables */}
              {preflightData.checks?.affectedTables && preflightData.checks.affectedTables.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Affected Tables:</h4>
                  <div className="flex flex-wrap gap-2">
                    {preflightData.checks.affectedTables.map((table: string) => (
                      <span key={table} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {table}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Migrations List */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Applying {preflightData.names?.length || 0} migration(s) to {preflightData.target}:
                </h4>
                <ul className="text-sm space-y-1">
                  {preflightData.names?.map((name: string) => (
                    <li key={name} className="font-mono">• {name}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowPreflightModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkApply}
                className={`px-4 py-2 text-white rounded ${
                  preflightData.ok
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {preflightData.ok ? 'Confirm & Apply' : 'Proceed Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-700', icon: '⏳' },
    applied: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌' },
    rolled_back: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '🔄' },
    running: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '⚡' }
  };

  const style = styles[status] || styles.pending;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 ${style.bg} ${style.text} text-xs rounded font-medium`}>
      {style.icon} {status.toUpperCase()}
    </span>
  );
};

const ChecksumBadge: React.FC<{ item: InventoryItem }> = ({ item }) => {
  if (item.isGhost) {
    return (
      <span className="text-purple-600" title="File missing from filesystem">
        <IoWarning size={20} />
      </span>
    );
  }

  if (item.checksumMatch === null) {
    return (
      <span className="text-gray-400" title="No checksum in database">
        <IoHelpCircle size={20} />
      </span>
    );
  }

  if (item.checksumMatch) {
    return (
      <span className="text-green-600" title="Checksum matches">
        <IoCheckmarkCircle size={20} />
      </span>
    );
  }

  return (
    <span className="text-orange-600" title="Checksum mismatch - file has changed!">
      <IoWarning size={20} />
    </span>
  );
};

export default InventoryMatrix;

